import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import client, { downloadFile } from "../lib/api";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, SolidCard, Loading, Badge, Gauge, fmtNum } from "../components/common";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Layers3, Timer, ShieldCheck, DollarSign, TrendingUp, Sparkles, Bell,
  Lightbulb, Server, ArrowUpRight, Loader2, X, FileDown,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import Markdown from "../components/Markdown";

const DONUT = ["#0e7490", "#06b6d4", "#22d3ee", "#67e8f9", "#a5f3fc"];

function Kpi({ icon: Icon, label, value, chip, chipUp = true, accent }) {
  return (
    <GlassCard hover className="p-5" data-testid={`kpi-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {chip != null && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-0.5 ${chipUp ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
            <TrendingUp className={`w-3 h-3 ${chipUp ? "" : "rotate-180"}`} />{chip}
          </span>
        )}
      </div>
      <div className="mt-4 text-3xl font-bold text-slate-900 font-display">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </GlassCard>
  );
}

function AiInsightModal({ open, onClose, datasetId }) {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  React.useEffect(() => {
    if (!open || !datasetId) return;
    setLoading(true); setText("");
    client.post("/ai/insight", { dataset_id: datasetId })
      .then(({ data }) => setText(data.insight))
      .catch(() => toast.error("Could not generate insight"))
      .finally(() => setLoading(false));
  }, [open, datasetId]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/20 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-strong rounded-3xl max-w-lg w-full p-7 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ai-insight-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl pm-gradient flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
            <div><div className="font-bold text-slate-900">AI Process Analyst</div><div className="text-xs text-slate-500">Narrated executive summary</div></div>
          </div>
          <button onClick={onClose} data-testid="close-insight" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="py-10 flex flex-col items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin text-cyan-600 mb-2" />Analysing process health…</div>
        ) : (
          <Markdown>{text}</Markdown>
        )}
      </motion.div>
    </div>
  );
}

function DashboardInner() {
  const { user } = useAuth();
  const { currentDataset } = useApp();
  const { data, loading } = useFetch("/dashboard/overview");
  const [insight, setInsight] = useState(false);

  if (loading) return <Loading />;
  if (!data) return null;
  const k = data.kpis;

  return (
    <div className="space-y-6 pm-fade-up">
      <AiInsightModal open={insight} onClose={() => setInsight(false)} datasetId={currentDataset?.id} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-cyan-700/80 mb-1">Process Intelligence Overview</div>
          <h1 className="text-3xl font-extrabold text-slate-900">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Live health across {k.total_cases.toLocaleString()} process cases and {fmtNum(k.total_events)} events.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-dashboard-pdf" onClick={async () => {
            if (!currentDataset) return toast.error("No dataset");
            try { await downloadFile(`/reports/${currentDataset.id}/pdf?report_type=Executive Dashboard`, `${currentDataset.name}.pdf`); toast.success("Report downloaded"); }
            catch { toast.error("Export failed"); }
          }} variant="outline" className="rounded-xl h-11 glass border-white/60">
            <FileDown className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button data-testid="generate-insight-btn" onClick={() => setInsight(true)}
            className="rounded-xl pm-gradient text-white border-0 pm-transition hover:opacity-95 h-11 px-5">
            <Sparkles className="w-4 h-4 mr-2" /> Generate AI Insight
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Kpi icon={Layers3} label="Total Cases" value={k.total_cases.toLocaleString()} chip="12%" accent="pm-gradient" />
        <Kpi icon={Timer} label="Avg Cycle Time" value={`${(k.avg_duration_hours / 24).toFixed(1)}d`} chip="8%" chipUp={false} accent="bg-gradient-to-br from-indigo-500 to-blue-500" />
        <Kpi icon={ShieldCheck} label="Conformance" value={`${k.conformance_rate}%`} chip="3%" accent="bg-gradient-to-br from-emerald-500 to-teal-500" />
        <Kpi icon={DollarSign} label="Potential Savings" value={`$${fmtNum(k.potential_savings)}`} chip="ROI" accent="bg-gradient-to-br from-amber-500 to-orange-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Trend */}
        <GlassCard className="lg:col-span-8 p-6">
          <div className="flex items-center justify-between mb-5">
            <div><h3 className="font-bold text-slate-900 text-lg">Throughput & Conformance</h3><p className="text-xs text-slate-500">Weekly cases vs. conformance rate</p></div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-cyan-500" />Cases</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400" />Conformance %</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend} margin={{ left: -20, right: 10 }}>
              <defs>
                <linearGradient id="gCases" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} /><stop offset="100%" stopColor="#06b6d4" stopOpacity={0} /></linearGradient>
                <linearGradient id="gConf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#818cf8" stopOpacity={0.3} /><stop offset="100%" stopColor="#818cf8" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }} />
              <Area type="monotone" dataKey="cases" stroke="#06b6d4" strokeWidth={2.5} fill="url(#gCases)" />
              <Area type="monotone" dataKey="conformance" stroke="#818cf8" strokeWidth={2.5} fill="url(#gConf)" />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>

        {/* Conformance gauge + domain */}
        <GlassCard className="lg:col-span-4 p-6 flex flex-col">
          <h3 className="font-bold text-slate-900 text-lg mb-1">Process Mix</h3>
          <p className="text-xs text-slate-500 mb-2">Cases by domain</p>
          <div className="flex-1 flex items-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.domain_distribution} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={3}>
                  {data.domain_distribution.map((_, i) => <Cell key={i} fill={DONUT[i % DONUT.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {data.domain_distribution.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: DONUT[i % DONUT.length] }} />{d.name}</span>
                <span className="font-semibold text-slate-800">{d.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Alerts */}
        <GlassCard className="lg:col-span-5 p-6">
          <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-red-500" /><h3 className="font-bold text-slate-900">Active Alerts</h3><span className="ml-auto text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">{k.open_alerts} open</span></div>
          <div className="space-y-3">
            {data.alerts.map((a) => (
              <div key={a.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/50 border border-white/60" data-testid={`alert-${a.id}`}>
                <div className={`w-1.5 h-full rounded-full ${a.severity === "high" ? "bg-red-500" : a.severity === "medium" ? "bg-amber-500" : "bg-sky-500"}`} style={{ minHeight: 40 }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="text-sm font-semibold text-slate-800 truncate">{a.title}</span><Badge kind="sev" value={a.severity} /></div>
                  <p className="text-xs text-slate-500 mt-0.5">{a.message}</p>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Recommendations */}
        <GlassCard className="lg:col-span-4 p-6">
          <div className="flex items-center gap-2 mb-4"><Lightbulb className="w-4 h-4 text-amber-500" /><h3 className="font-bold text-slate-900">Top Recommendations</h3></div>
          <div className="space-y-3">
            {data.recommendations.map((r) => (
              <div key={r.id} className="p-3 rounded-xl bg-white/50 border border-white/60 pm-transition pm-lift" data-testid={`rec-${r.id}`}>
                <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-800">{r.title}</span><Badge kind="impact" value={r.impact} /></div>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span className="text-emerald-600 font-semibold flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" />{r.estimated_roi_percent}% ROI</span>
                  <span>${fmtNum(r.estimated_savings_usd)} saved</span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Connector health */}
        <GlassCard className="lg:col-span-3 p-6">
          <div className="flex items-center gap-2 mb-4"><Server className="w-4 h-4 text-cyan-600" /><h3 className="font-bold text-slate-900">Connectors</h3></div>
          <div className="space-y-2.5">
            {data.connectors.slice(0, 6).map((c) => (
              <div key={c.id} className="flex items-center justify-between">
                <span className="text-sm text-slate-700 truncate">{c.name}</span>
                <span className={`w-2 h-2 rounded-full ${c.status === "connected" ? "bg-emerald-500 pm-pulse" : c.status === "syncing" ? "bg-sky-500" : "bg-red-500"}`} />
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

export default function Dashboard() {
  return <NeedTenant><DashboardInner /></NeedTenant>;
}
